import {
  OUTPUT_LOG_HTTP_METHODS,
  OUTPUT_LOG_PROVIDER_AZURE_LOGIC_APP,
  type OutputLogBodyField,
  type OutputLogHttpMethod
} from '../types/output-log'

export {
  OUTPUT_LOG_HTTP_METHODS,
  OUTPUT_LOG_PROVIDER_AZURE_LOGIC_APP
} from '../types/output-log'
export type {
  IntegrationOutputLog,
  OutputLogBodyField,
  OutputLogHttpMethod,
  OutputLogProvider
} from '../types/output-log'

/** Headers API Management must set so the audit record can use the caller-facing request. */
export const OE_HEADER_ORIGINAL_URL = 'x-oe-original-url' as const
export const OE_HEADER_SUBSCRIPTION_ID = 'x-oe-subscription-id' as const
export const OE_HEADER_CLIENT_IP = 'x-oe-client-ip' as const

export const OUTPUT_LOG_RESERVED_BODY_KEYS = [
  'timestamp',
  'trackingId',
  'method',
  'url',
  'status',
  'latencyMs',
  'apimSubscriptionId',
  'clientIp',
  'requestBody',
  'responseBody',
  'requestBodyOmitted',
  'responseBodyOmitted'
] as const

const OUTPUT_LOG_BODY_FIELD_LIMIT = 20
const OUTPUT_LOG_BODY_KEY_PATTERN = /^[A-Za-z][A-Za-z0-9_-]{0,63}$/
const OUTPUT_LOG_FORBIDDEN_BODY_KEYS = new Set(['constructor', 'prototype', '__proto__'])

export function isOutputLogHttpMethod(value: string): value is OutputLogHttpMethod {
  return (OUTPUT_LOG_HTTP_METHODS as readonly string[]).includes(value)
}

export function outputLogUrlError(url: string): string | undefined {
  let parsed: URL
  try {
    parsed = new URL(url)
  } catch {
    return 'Logic App URL must be a valid URL'
  }

  if (parsed.protocol !== 'https:') {
    return 'Logic App URL must use https'
  }

  if (parsed.username || parsed.password) {
    return 'Logic App URL must not include a username or password'
  }

  const host = parsed.hostname.toLowerCase()
  if (host !== 'logic.azure.com' && !host.endsWith('.logic.azure.com')) {
    return 'Logic App URL host must be a logic.azure.com host'
  }

  return undefined
}

export function outputLogConfigError(config: {
  provider: string
  method: string
  url: string
  bodyFields: OutputLogBodyField[]
}): string | undefined {
  if (config.provider !== OUTPUT_LOG_PROVIDER_AZURE_LOGIC_APP) {
    return 'outputLog.provider must be azure_logic_app'
  }

  if (!isOutputLogHttpMethod(config.method)) {
    return 'outputLog.method must be POST, PUT, or PATCH'
  }

  const urlError = outputLogUrlError(config.url.trim())
  if (urlError) {
    return urlError
  }

  if (config.bodyFields.length > OUTPUT_LOG_BODY_FIELD_LIMIT) {
    return `outputLog supports at most ${OUTPUT_LOG_BODY_FIELD_LIMIT} body fields`
  }

  const seenKeys = new Set<string>()
  for (const field of config.bodyFields) {
    const key = field.key.trim()
    if (!OUTPUT_LOG_BODY_KEY_PATTERN.test(key) || OUTPUT_LOG_FORBIDDEN_BODY_KEYS.has(key)) {
      return 'Body field key must start with a letter and contain only letters, numbers, underscores, and hyphens'
    }

    if ((OUTPUT_LOG_RESERVED_BODY_KEYS as readonly string[]).includes(key)) {
      return `outputLog body field key is reserved: ${key}`
    }

    if (seenKeys.has(key)) {
      return 'outputLog body field keys must be unique'
    }
    seenKeys.add(key)

    if (field.value.length > 4000) {
      return `Body field ${key} is too long`
    }
  }

  return undefined
}
